import BackendService from 'BackendService';
import { useCallback, useEffect, useState } from 'react';
import { applyEventToggle, buildSubscriptionsPayload } from './notificationSubscriptionUtils';
import { EventType } from './settingsTypes';

interface UseNotificationSubscriptionsOptions {
  onLoadError: (message: string) => void;
  onSaveSuccess: (message: string) => void;
}

export function useNotificationSubscriptions({
  onLoadError,
  onSaveSuccess,
}: UseNotificationSubscriptionsOptions) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState<EventType[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasAutoSavedSeen, setHasAutoSavedSeen] = useState(false);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      setLoading(true);
      onLoadError('');
      const status = await new BackendService().getNotificationSubscriptionStatus();
      setEvents(status.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      onLoadError(
        error instanceof Error ? error.message : 'Failed to load notification preferences',
      );
    } finally {
      setLoading(false);
    }
  }, [onLoadError]);

  useEffect(() => {
    void fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  useEffect(() => {
    const autoSaveSeenStatus = async () => {
      if (loading || hasAutoSavedSeen || events.length === 0) {
        return;
      }

      const unseenItems = events.filter((event) => !event.seen);
      if (unseenItems.length === 0) {
        return;
      }

      setHasAutoSavedSeen(true);

      try {
        const subscriptions = buildSubscriptionsPayload(events);
        Object.keys(subscriptions).forEach((id) => {
          const value = subscriptions[id];
          if (typeof value === 'object') {
            subscriptions[id] = { ...value, seen: true };
          }
        });
        await new BackendService().saveNotificationSubscriptions(subscriptions);
      } catch (error) {
        console.error('Failed to auto-save seen status:', error);
      }
    };

    void autoSaveSeenStatus();
  }, [loading, events, hasAutoSavedSeen]);

  const handleToggle = (eventTypeId: string) => {
    setEvents((prev) => applyEventToggle(prev, eventTypeId));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await new BackendService().saveNotificationSubscriptions(buildSubscriptionsPayload(events));
      onSaveSuccess('Notification preferences saved successfully!');
      setHasChanges(false);
      await fetchSubscriptionStatus();
    } catch (error) {
      console.error('Failed to save subscriptions:', error);
      onLoadError(
        error instanceof Error ? error.message : 'Failed to save notification preferences',
      );
      await fetchSubscriptionStatus();
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    events,
    hasChanges,
    handleToggle,
    handleSave,
  };
}
